import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import { useScreenShake, SCENE } from '../../lib/battle3d';
import CommandRoom from '../arena/CommandRoom';
import TonTable from '../arena/TonTable';
import Card3D from '../arena/Card3D';
import GameIntro from '../arena/GameIntro';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ton`;

// ─── Card metadata ──────────────────────────────────────────────────────────
const TYPE_META = {
  positive:   { bg:'linear-gradient(160deg,#062c12,#0a4a1c)', border:'#22c55e', glow:'rgba(34,197,94,0.6)',   text:'#4ade80', getLabel:(v)=>`+${v}` },
  negative:   { bg:'linear-gradient(160deg,#2c0606,#4a0a0a)', border:'#ef4444', glow:'rgba(239,68,68,0.6)',   text:'#f87171', getLabel:(v)=>`${v}` },
  neutral:    { bg:'linear-gradient(160deg,#1a1a1a,#262626)', border:'#71717a', glow:'rgba(113,113,122,0.4)', text:'#d4d4d8', getLabel:()=>'0' },
  jolly:      { bg:'linear-gradient(160deg,#1e0b42,#2d1163)', border:'#a855f7', glow:'rgba(168,85,247,0.6)', text:'#c084fc', getLabel:()=>'🎭' },
  extra_life: { bg:'linear-gradient(160deg,#3b0012,#580019)', border:'#f43f5e', glow:'rgba(244,63,94,0.6)',  text:'#fb7185', getLabel:()=>'❤' },
  double:     { bg:'linear-gradient(160deg,#021a30,#022d4e)', border:'#38bdf8', glow:'rgba(56,189,248,0.6)', text:'#7dd3fc', getLabel:()=>'×2' },
  reverse:    { bg:'linear-gradient(160deg,#0e1f02,#182e03)', border:'#a3e635', glow:'rgba(163,230,53,0.5)', text:'#bef264', getLabel:()=>'🔄' },
  back:       { bg:'linear-gradient(160deg,#050d28,#081435)', border:'rgba(96,165,250,0.55)', glow:'rgba(96,165,250,0.45)', text:'#93c5fd', getLabel:()=>'?' },
};

const getLabel = (card) => TYPE_META[card?.type]?.getLabel(card?.value) ?? card?.label ?? '?';

// Back of every card in the deck, plus the cut-edge colours
const TON_BACK = { deep:'#00081c', mid:'#062046', rim:'#2f6fd0', gold:'#9ecbff', glow:'rgba(60,140,255,0.45)', emblem:'✦' };
const TON_EDGE = ['#dbeafe', '#6b93c9', '#25415f'];

// How hard the room shakes for each kind of card
const SHAKE_BY_TYPE = { negative: 1.1, positive: 0.5, extra_life: 0.7, double: 0.9, reverse: 0.6, jolly: 0.5, neutral: 0 };

// ─── Console readout: which values are still live on the board ───────────────
const CardTable = ({ tableCards, mobile }) => {
  const unrevLabels = tableCards.filter(c => !c.revealed).map(getLabel);
  for (let i = unrevLabels.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [unrevLabels[i], unrevLabels[j]] = [unrevLabels[j], unrevLabels[i]];
  }
  let ui = 0;
  const cardInfo = tableCards.map((c, i) => ({
    ...c, idx: i,
    displayLabel: c.revealed ? getLabel(c) : (unrevLabels[ui++] ?? '?'),
  }));

  return (
    <motion.div
      initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
      style={{
        position: 'relative', overflow: 'hidden', display: 'inline-block',
        borderRadius: mobile ? 8 : 12,
        padding: mobile ? '5px 12px 6px' : '9px 26px 11px',
        marginBottom: mobile ? 8 : 14,
        background: 'linear-gradient(165deg, rgba(14,28,52,0.96) 0%, rgba(4,10,22,0.98) 100%)',
        boxShadow: '0 0 0 1px rgba(96,165,250,0.4), 0 6px 20px rgba(0,0,0,0.8), inset 0 1px 0 rgba(180,214,255,0.14), inset 0 -8px 18px rgba(0,0,0,0.6)',
      }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:'45%', background:'linear-gradient(to bottom, rgba(180,214,255,0.07), transparent)', pointerEvents:'none' }} />
      <div style={{
        fontSize: mobile ? 6.5 : 8.5, fontWeight: 800, letterSpacing: '0.24em',
        color: 'rgba(147,197,253,0.5)', textTransform: 'uppercase', textAlign: 'center',
        marginBottom: mobile ? 2 : 4,
      }}>
        ◄ Valori in Gioco ►
      </div>
      <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap: mobile ? 10 : 20, flexWrap:'wrap' }}>
        {cardInfo.map((c, i) => (
          <motion.span key={i}
            animate={c.revealed ? { opacity: 0.4, scale: 0.88 } : { opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            style={{
              fontFamily: "'Cinzel',serif", fontSize: mobile ? 15 : 24, fontWeight: 900,
              color: c.revealed ? 'rgba(90,110,140,0.55)' : '#bfdbfe',
              textDecorationLine: c.revealed ? 'line-through' : 'none',
              textDecorationColor: 'rgba(160,40,30,0.75)',
              textDecorationThickness: '2px',
              textShadow: c.revealed ? 'none' : '0 2px 0 rgba(10,30,70,1), 0 3px 6px rgba(0,0,0,0.95), 0 0 14px rgba(96,165,250,0.5)',
            }}>
            {c.displayLabel ?? getLabel(c)}
          </motion.span>
        ))}
      </div>
    </motion.div>
  );
};

// ─── Table card: the shared 3D body with the value struck on the face ────────
const TableCard = ({ card, index, canPick, onPick, mobile, isRevealing }) => {
  const revealed = card?.revealed;
  const meta = TYPE_META[card?.type] || TYPE_META.neutral;
  const label = getLabel(card);

  const front = (
    <div style={{
      position: 'absolute', inset: 0, background: meta.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:'40%', background:'linear-gradient(to bottom, rgba(255,255,255,0.1), transparent)' }} />
      <div style={{
        position:'absolute', top: mobile?5:8, left: mobile?6:10,
        fontSize: mobile?11:15, fontWeight:900, color: meta.text,
        fontFamily:"'Cinzel',serif", textShadow:`0 0 10px ${meta.glow}`,
      }}>{label}</div>
      <div style={{
        position:'absolute', bottom: mobile?5:8, right: mobile?6:10,
        fontSize: mobile?11:15, fontWeight:900, color: meta.text,
        fontFamily:"'Cinzel',serif", transform:'rotate(180deg)', textShadow:`0 0 10px ${meta.glow}`,
      }}>{label}</div>
      <div style={{
        position:'absolute', width: mobile?46:78, height: mobile?46:78, borderRadius:'50%',
        background:`radial-gradient(circle, ${meta.glow} 0%, transparent 70%)`,
      }} />
      <div style={{
        fontSize: mobile?28:48, fontWeight:900, color: meta.text, position:'relative',
        fontFamily:"'Cinzel',serif", textShadow:`0 0 24px ${meta.glow}`, lineHeight:1,
      }}>
        {label}
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: -70, rotateZ: index % 2 ? 9 : -9 }}
      animate={{ opacity: 1, y: 0, rotateZ: 0 }}
      transition={{ delay: 0.25 + index * 0.11, type: 'spring', stiffness: 150, damping: 15 }}
      style={{ transformStyle: 'preserve-3d' }}
    >
      <Card3D
        revealed={revealed}
        index={index}
        onPick={onPick}
        canPick={canPick}
        isRevealing={isRevealing}
        mobile={mobile}
        back={TON_BACK}
        edge={TON_EDGE}
        tone={meta.border}
        front={front}
        prompt="SCEGLI"
      />
    </motion.div>
  );
};

// ─── Vertical battery meter ──────────────────────────────────────────────────
const getPtColor = (pts) => {
  if (pts <= 0) return { bar:'#6b7280', glow:'rgba(107,114,128,0.4)' };
  if (pts <= 2) return { bar:'#ef4444', glow:'rgba(239,68,68,0.8)' };
  if (pts <= 4) return { bar:'#f97316', glow:'rgba(249,115,22,0.7)' };
  if (pts <= 6) return { bar:'#22c55e', glow:'rgba(34,197,94,0.7)' };
  if (pts <= 8) return { bar:'#fbbf24', glow:'rgba(251,191,36,0.7)' };
  return { bar:'#ef4444', glow:'rgba(239,68,68,0.9)' };
};

const Meter = ({ points, h=100, w=16 }) => {
  const pts = Math.max(0, Math.min(10, points));
  const pct = pts / 10;
  const { bar, glow } = getPtColor(pts);
  const danger = pts <= 1 || pts >= 9;
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:5, flexShrink:0 }}>
      <div style={{ position:'relative', width:w, height:h, borderRadius:w/2,
        background:'rgba(0,0,0,0.75)', border:'1px solid rgba(255,255,255,0.12)',
        overflow:'hidden', boxShadow:`inset 0 3px 8px rgba(0,0,0,0.9),inset 0 0 0 1px rgba(255,255,255,0.05)` }}>
        {[...Array(9)].map((_,i) => (
          <div key={i} style={{ position:'absolute', left:2, right:2, bottom:`${(i+1)*10}%`, height:1,
            background: i===4 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.07)' }} />
        ))}
        <motion.div
          animate={{ height:`${pct*100}%` }}
          transition={{ type:'spring', stiffness:180, damping:20 }}
          style={{ position:'absolute', bottom:0, left:0, right:0,
            background:`linear-gradient(to top,${bar},${bar}cc)`,
            boxShadow:`0 0 14px ${glow}, 0 -2px 8px ${glow}`,
            borderRadius:`0 0 ${w/2}px ${w/2}px` }}
        />
        {danger && (
          <motion.div animate={{ opacity:[0,1,0] }} transition={{ duration:0.45, repeat:Infinity }}
            style={{ position:'absolute', bottom:`${pct*100}%`, left:0, right:0, height:3,
              background:bar, boxShadow:`0 0 8px ${glow}` }} />
        )}
      </div>
      <motion.span key={pts} initial={{ scale:1.8, opacity:0 }} animate={{ scale:1, opacity:1 }}
        transition={{ type:'spring', stiffness:300, damping:18 }}
        style={{ fontSize:w+3, fontWeight:900, color:bar,
          fontFamily:"'Cinzel',serif", textShadow:`0 0 10px ${glow}`, lineHeight:1 }}>
        {pts}
      </motion.span>
    </div>
  );
};

// ─── Player Box ──────────────────────────────────────────────────────────────
const PlayerBox = ({ player, isActive, variant='bottom' }) => {
  const cfg = {
    bottom: { mH:130, mW:20, hS:26, jS:26, nS:15, pad:'18px 20px', minW:300, gap:14 },
    top:    { mH:90,  mW:15, hS:20, jS:20, nS:12, pad:'14px 16px', minW:240, gap:11 },
    side:   { mH:70,  mW:12, hS:16, jS:16, nS:10, pad:'12px 12px', minW:130, gap:8  },
  }[variant] || {};

  const pts = player.eliminated ? 0 : player.points;
  const { bar } = getPtColor(pts);
  const isSide = variant === 'side';

  return (
    <div style={{
      position:'relative', borderRadius:16, overflow:'hidden',
      background:'linear-gradient(145deg,#1a2a4a 0%,#0f1e38 60%,#0a1628 100%)',
      border:`2px solid ${isActive ? 'rgba(96,165,250,0.9)' : 'rgba(96,165,250,0.25)'}`,
      boxShadow: isActive
        ? '0 0 0 2px rgba(96,165,250,0.4),0 0 36px rgba(96,165,250,0.35),0 8px 32px rgba(0,0,0,0.8)'
        : '0 0 0 1px rgba(96,165,250,0.12),0 6px 28px rgba(0,0,0,0.7)',
      padding: cfg.pad, minWidth: cfg.minW,
      display:'flex', alignItems:'center', gap: cfg.gap,
    }}>
      {/* Gloss */}
      <div style={{ position:'absolute', top:0, left:0, right:0, height:'45%',
        background:'linear-gradient(to bottom,rgba(255,255,255,0.08),transparent)',
        borderRadius:'16px 16px 0 0', pointerEvents:'none' }} />
      {/* Side accent stripe */}
      <div style={{ position:'absolute', top:0, left:0, bottom:0, width:3,
        background:`linear-gradient(to bottom,${bar},${bar}44)`,
        borderRadius:'16px 0 0 16px', opacity: player.eliminated ? 0.2 : 0.8 }} />

      {/* Meter */}
      <Meter points={pts} h={cfg.mH} w={cfg.mW} />

      {/* Info column */}
      <div style={{ display:'flex', flexDirection:'column', gap: isSide ? 6 : 9, flex:1, minWidth:0 }}>
        {/* Name */}
        <div style={{ fontSize:cfg.nS, fontWeight:800,
          color: player.eliminated ? '#4b5563' : '#e8f4ff',
          fontFamily:"'Cinzel',serif",
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          letterSpacing:'0.04em',
          textShadow: player.eliminated ? 'none' : '0 0 14px rgba(147,197,253,0.5)' }}>
          {player.username}
        </div>

        {/* Hearts */}
        <div style={{ display:'flex', gap: isSide?3:5, alignItems:'center', flexWrap:'wrap' }}>
          {[...Array(5)].map((_,i) => (
            <img key={i} src="/images/icons/heart.svg" alt="vita"
              style={{ width:cfg.hS, height:cfg.hS,
                opacity: (!player.eliminated && i < player.lives) ? 1 : 0.1,
                filter: (!player.eliminated && i < player.lives)
                  ? 'drop-shadow(0 0 5px rgba(239,68,68,0.8)) drop-shadow(0 0 2px rgba(239,68,68,1))'
                  : 'grayscale(1)',
                transition:'all 0.3s' }}
            />
          ))}
        </div>

        {/* Jolly hats */}
        <div style={{ display:'flex', gap: isSide?3:5, alignItems:'center' }}>
          {[...Array(3)].map((_,i) => (
            <img key={i} src="/images/icons/jolly.png" alt="jolly"
              style={{ width:cfg.jS, height:cfg.jS,
                opacity: (!player.eliminated && i < player.jolly) ? 1 : 0.1,
                filter: (!player.eliminated && i < player.jolly)
                  ? 'drop-shadow(0 0 5px rgba(168,85,247,0.8)) drop-shadow(0 0 2px rgba(168,85,247,1))'
                  : 'grayscale(1)',
                transition:'all 0.3s' }}
            />
          ))}
        </div>

        {/* Points bar */}
        {!player.eliminated && (
          <div style={{ height:4, borderRadius:2, background:'rgba(255,255,255,0.08)', overflow:'hidden' }}>
            <motion.div
              animate={{ width:`${(pts/10)*100}%` }}
              transition={{ type:'spring', stiffness:200, damping:22 }}
              style={{ height:'100%', borderRadius:2,
                background:`linear-gradient(to right,${bar}88,${bar})`,
                boxShadow:`0 0 8px ${getPtColor(pts).glow}` }}
            />
          </div>
        )}
      </div>

      {/* TURN badge */}
      {isActive && !player.eliminated && (
        <motion.div
          animate={{ opacity:[0.5,1,0.5], boxShadow:['0 0 4px rgba(96,165,250,0.3)','0 0 16px rgba(96,165,250,1)','0 0 4px rgba(96,165,250,0.3)'] }}
          transition={{ duration:0.9, repeat:Infinity }}
          style={{ position:'absolute', top:8, right:10,
            background:'rgba(96,165,250,0.14)', border:'1px solid rgba(96,165,250,0.8)',
            borderRadius:6, padding:'3px 8px',
            fontSize:9, fontWeight:900, color:'#93c5fd', letterSpacing:'0.12em', textTransform:'uppercase' }}>
          TURN
        </motion.div>
      )}

      {/* Pulse ring */}
      {isActive && !player.eliminated && (
        <motion.div animate={{ opacity:[0.1,0.7,0.1] }} transition={{ duration:0.9, repeat:Infinity }}
          style={{ position:'absolute', inset:0, borderRadius:16,
            border:'2px solid rgba(96,165,250,0.8)', pointerEvents:'none' }} />
      )}

      {/* Eliminated overlay */}
      {player.eliminated && (
        <div style={{ position:'absolute', inset:0, zIndex:8, background:'rgba(0,0,0,0.82)',
          borderRadius:16, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ position:'absolute', inset:0, display:'flex', justifyContent:'space-evenly', padding:'0 10px' }}>
            {[...Array(8)].map((_,i) => (
              <div key={i} style={{ width:1.5, background:'linear-gradient(to bottom,#1f2937,#0f172a)', borderRadius:1 }} />
            ))}
          </div>
          <span style={{ position:'relative', zIndex:1, fontSize:11, fontWeight:900,
            color:'#4b5563', letterSpacing:'0.15em', textTransform:'uppercase',
            background:'rgba(0,0,0,0.85)', padding:'3px 12px', borderRadius:4 }}>
            ELIMINATO
          </span>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const TonGame = ({ user }) => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [mobile, setMobile] = useState(window.innerWidth < 680);
  const [revealing, setRevealing] = useState(null);
  const [showIntro, setShowIntro] = useState(() => sessionStorage.getItem(`ton_intro_${gameId}`) !== 'done');
  const pollingRef = useRef(null);
  const { shakeControls, shake } = useScreenShake();

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 680);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const loadGame = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/game/${gameId}`);
      setGameState(res.data); setLoading(false);
    } catch { toast.error('Errore caricamento partita'); }
  }, [gameId]);

  useEffect(() => {
    loadGame();
    pollingRef.current = setInterval(loadGame, 2500);
    const ch = supabase.channel(`ton_${gameId}`)
      .on('postgres_changes', { event:'UPDATE', schema:'public', table:'ton_game_sessions', filter:`id=eq.${gameId}` },
        (p) => setGameState(p.new)).subscribe();
    return () => { clearInterval(pollingRef.current); ch.unsubscribe(); supabase.removeChannel(ch); };
  }, [loadGame, gameId]);

  const handlePickCard = async (cardIndex) => {
    if (actionInProgress) return;
    setActionInProgress(true);
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    try {
      const res = await axios.post(`${API}/game/pick-card`, { game_id:gameId, user_id:user.user_id, card_index:cardIndex });
      setRevealing(cardIndex);
      setFeedback(res.data.message);
      const fresh = await axios.get(`${API}/game/${gameId}`);
      setGameState(fresh.data);
      const picked = (fresh.data.table_cards || [])[cardIndex];
      const power = SHAKE_BY_TYPE[picked?.type] ?? 0;
      if (power) setTimeout(() => shake(power), 700);
      setTimeout(() => setRevealing(null), 1000);
      setTimeout(async () => {
        setFeedback(null); await loadGame();
        pollingRef.current = setInterval(loadGame, 2500);
      }, 3500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
      pollingRef.current = setInterval(loadGame, 2500);
    } finally { setActionInProgress(false); }
  };

  const handleUseJolly = async () => {
    if (actionInProgress) return;
    setActionInProgress(true);
    try {
      await axios.post(`${API}/game/use-jolly`, { game_id:gameId, user_id:user.user_id });
      setFeedback('Turno saltato con il Jolly!');
      setTimeout(() => setFeedback(null), 2500);
      await loadGame();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setActionInProgress(false); }
  };

  if (loading || !gameState) return (
    <div style={{ width:'100dvw', height:'100dvh', position:'relative', overflow:'hidden', background:'#01050e' }}>
      <CommandRoom mobile={mobile} dimmed />
      <div style={{ position:'relative', zIndex:2, height:'100%', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:14 }}>
        <motion.div
          animate={{ rotate:[0,360] }} transition={{ duration:6, repeat:Infinity, ease:'linear' }}
          style={{ fontSize:42, filter:'drop-shadow(0 0 22px rgba(96,165,250,0.7))' }}>
          ✦
        </motion.div>
        <div style={{ fontFamily:"'Cinzel',serif", fontSize:16, fontWeight:800, letterSpacing:'0.16em', color:'#bfdbfe' }}>
          Calibrazione della console…
        </div>
      </div>
    </div>
  );

  const players = gameState.players || [];
  const currentPlayer = players[gameState.current_turn];
  const myPlayer = players.find(p => p.user_id === user.user_id);
  const isMyTurn = currentPlayer?.user_id === user.user_id;
  const canAct = isMyTurn && !myPlayer?.eliminated && !actionInProgress;
  const tableCards = gameState.table_cards || [];
  const myIndex = players.findIndex(p => p.user_id === user.user_id);
  const pos = {};
  players.forEach((p,i) => { const rel=(i-myIndex+players.length)%players.length; pos[['bottom','right','top','left'][rel]]=p; });

  if (gameState.status === 'finished') {
    const winner = players.find(p => p.user_id === gameState.winner);
    const isMe = winner?.user_id === user.user_id;
    return (
      <div style={{ minHeight:'100dvh', background:'linear-gradient(135deg,#0a1628,#0d1e38)',
        display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
        <motion.div initial={{ scale:0.2, opacity:0, y:60 }} animate={{ scale:1, opacity:1, y:0 }}
          transition={{ type:'spring', stiffness:180, damping:18 }}
          style={{ background:'linear-gradient(160deg,rgba(16,28,56,0.99),rgba(10,18,38,0.99))',
            borderRadius:24, padding:'48px clamp(24px,6vw,56px)',
            border:`2px solid ${isMe?'rgba(96,165,250,0.7)':'rgba(255,255,255,0.1)'}`,
            boxShadow:isMe?'0 0 60px rgba(96,165,250,0.2),0 40px 80px rgba(0,0,0,0.9)':'0 40px 80px rgba(0,0,0,0.9)',
            textAlign:'center', maxWidth:400, width:'100%' }}>
          <motion.div animate={{ rotate:isMe?[0,10,-10,8,-8,0]:0 }} transition={{ duration:1, delay:0.5 }}
            style={{ fontSize:64, marginBottom:16 }}>{isMe?'🏆':'💀'}</motion.div>
          <div style={{ fontSize:9, fontWeight:800, letterSpacing:'0.22em',
            color:isMe?'rgba(96,165,250,0.6)':'rgba(255,255,255,0.25)',
            textTransform:'uppercase', marginBottom:10 }}>
            {isMe?'Vittoria':'Sconfitta'}
          </div>
          <h1 style={{ fontFamily:"'Cinzel',serif", fontSize:'clamp(22px,5vw,36px)', fontWeight:900, margin:'0 0 10px',
            background:isMe?'linear-gradient(to bottom,#fff,#60a5fa)':'linear-gradient(to bottom,#e5e7eb,#9ca3af)',
            WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
            {isMe?'Hai Vinto!':(winner?.username||'Fine')}
          </h1>
          <p style={{ color:'rgba(255,255,255,0.35)', fontSize:14, margin:'0 0 32px' }}>
            {isMe?"Sei rimasto l'ultimo in piedi.":`${winner?.username} ha vinto la partita.`}
          </p>
          <motion.button onClick={() => navigate('/games')} whileHover={{ scale:1.04 }} whileTap={{ scale:0.97 }}
            style={{ background:'linear-gradient(145deg,#2563eb,#1d4ed8)', border:'none', borderRadius:12,
              padding:'13px 36px', fontSize:13, fontWeight:900, color:'#fff', cursor:'pointer',
              fontFamily:"'Cinzel',serif", letterSpacing:'0.08em', textTransform:'uppercase',
              boxShadow:'0 0 24px rgba(96,165,250,0.4)' }}>
            Torna ai Giochi
          </motion.button>
        </motion.div>
      </div>
    );
  }

  const JollyBtn = () => myPlayer && myPlayer.jolly > 0 && canAct ? (
    <motion.button onClick={handleUseJolly} disabled={actionInProgress}
      whileHover={{ scale:1.04 }} whileTap={{ scale:0.96 }}
      animate={{ boxShadow:['0 0 8px rgba(139,92,246,0.35)','0 0 24px rgba(139,92,246,0.9)','0 0 8px rgba(139,92,246,0.35)'] }}
      transition={{ duration:1.6, repeat:Infinity }}
      style={{ display:'flex', alignItems:'center', gap:9, padding:mobile?'7px 16px':'9px 22px',
        background:'linear-gradient(135deg,#3b0764,#6d28d9)',
        border:'1px solid rgba(167,139,250,0.5)', borderRadius:11, cursor:'pointer',
        opacity:actionInProgress?0.5:1 }}>
      <img src="/images/icons/jolly.png" alt="jolly"
        style={{ width:mobile?16:20, height:mobile?16:20, filter:'drop-shadow(0 0 6px rgba(168,85,247,0.9))' }} />
      <span style={{ fontSize:mobile?11:13, fontWeight:800, color:'#e9d5ff', letterSpacing:'0.05em' }}>
        Salta il turno
      </span>
    </motion.button>
  ) : null;

  const Feedback = () => (
    <div style={{ minHeight:38, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <AnimatePresence mode="wait">
        {feedback && (
          <motion.div key="fb"
            initial={{ opacity:0, y:8, scale:0.95 }} animate={{ opacity:1, y:0, scale:1 }} exit={{ opacity:0 }}
            style={{ background:'linear-gradient(145deg,rgba(26,42,74,0.95),rgba(15,30,56,0.95))',
              backdropFilter:'blur(8px)', border:'1px solid rgba(96,165,250,0.4)',
              borderRadius:10, padding:'9px 24px', color:'#93c5fd',
              fontSize:mobile?12:14, fontWeight:700,
              boxShadow:'0 0 24px rgba(96,165,250,0.2)', textAlign:'center', maxWidth:'90vw' }}>
            {feedback}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const BG = () => <CommandRoom mobile={mobile} />;

  // Where each console sits around the table in 3D space
  const seat = (pos) => (mobile
    ? { top:'translateZ(-70px)', left:'rotateY(14deg)', right:'rotateY(-14deg)', bottom:'translateZ(16px)' }
    : {
        top:'translateZ(-230px) rotateX(8deg)',
        left:'rotateY(21deg) translateZ(-30px)',
        right:'rotateY(-21deg) translateZ(-30px)',
        bottom:'translateZ(45px) rotateX(-5deg)',
      })[pos];

  const Intro = () => (
    <AnimatePresence>
      {showIntro && (
        <GameIntro
          emblem="✦"
          title="Ten or Nothing"
          tagline="Né zero né dieci"
          rule="Resta tra 1 e 9 — ai limiti perdi una vita"
          cry="SI PARTE"
          players={players.map(p => p.username)}
          theme={{ accent:'#60a5fa', accentSoft:'rgba(96,165,250,0.55)', text:'#dbeafe', ring:'rgba(96,165,250,0.55)' }}
          onDone={() => { sessionStorage.setItem(`ton_intro_${gameId}`, 'done'); setShowIntro(false); }}
        />
      )}
    </AnimatePresence>
  );

  const RoundBadge = () => (
    <div style={{ position:'fixed', top:14, right:14, zIndex:30,
      background:'rgba(13,28,54,0.95)', backdropFilter:'blur(10px)',
      border:'1px solid rgba(96,165,250,0.25)', borderRadius:9, padding:'5px 15px',
      fontSize:11, color:'rgba(147,197,253,0.8)', fontWeight:700, letterSpacing:'0.08em' }}>
      Round {gameState.round||1} &nbsp;{gameState.direction===-1?'←':'→'}
    </div>
  );

  const WaitingMsg = () => !isMyTurn && !myPlayer?.eliminated ? (
    <div style={{ background:'rgba(13,24,46,0.9)', backdropFilter:'blur(8px)',
      border:'1px solid rgba(96,165,250,0.14)', borderRadius:9999,
      padding:mobile?'7px 20px':'9px 26px', color:'rgba(147,197,253,0.5)',
      fontSize:mobile?12:14, fontWeight:600 }}>
      Turno di <span style={{ color:'#93c5fd', fontWeight:700 }}>{currentPlayer?.username}</span>...
    </div>
  ) : null;

  // ── MOBILE ──────────────────────────────────────────────────────────────
  if (mobile) return (
    <div style={{ width:'100dvw', height:'100dvh', position:'relative', overflow:'hidden', background:'#01050e' }}>
      <BG/>
      <RoundBadge/>
      <motion.div animate={shakeControls} style={{
        position:'relative', zIndex:10, height:'100%', display:'flex', flexDirection:'column',
        perspective: SCENE.perspectiveMobile, perspectiveOrigin:'50% 38%',
      }}>
        <div style={{ display:'flex', justifyContent:'center', padding:'10px 10px 0', flexShrink:0, transformStyle:'preserve-3d' }}>
          {pos.top && (
            <div style={{ transform:seat('top'), transformStyle:'preserve-3d' }}>
              <PlayerBox player={pos.top} isActive={currentPlayer?.user_id===pos.top.user_id} variant="top"/>
            </div>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 8px', flex:1, minHeight:0, transformStyle:'preserve-3d' }}>
          {pos.left && (
            <div style={{ flexShrink:0, transform:seat('left'), transformStyle:'preserve-3d' }}>
              <PlayerBox player={pos.left} isActive={currentPlayer?.user_id===pos.left.user_id} variant="side"/>
            </div>
          )}
          <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:6, minWidth:0 }}>
            <div style={{ textAlign:'center' }}><CardTable tableCards={tableCards} mobile/></div>
            <TonTable mobile active={canAct}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(2,auto)', rowGap:16, columnGap:12, justifyContent:'center', justifyItems:'center', transformStyle:'preserve-3d' }}>
                {tableCards.map((card,i) => (
                  <TableCard key={i} card={card} index={i} canPick={canAct&&!card.revealed}
                    onPick={handlePickCard} mobile isRevealing={revealing===i}/>
                ))}
              </div>
            </TonTable>
            <Feedback/>
            <WaitingMsg/>
          </div>
          {pos.right && (
            <div style={{ flexShrink:0, transform:seat('right'), transformStyle:'preserve-3d' }}>
              <PlayerBox player={pos.right} isActive={currentPlayer?.user_id===pos.right.user_id} variant="side"/>
            </div>
          )}
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8, padding:'8px 10px 16px', flexShrink:0, transformStyle:'preserve-3d' }}>
          <JollyBtn/>
          {pos.bottom && (
            <div style={{ transform:seat('bottom'), transformStyle:'preserve-3d' }}>
              <PlayerBox player={pos.bottom} isActive={currentPlayer?.user_id===pos.bottom.user_id} variant="top"/>
            </div>
          )}
        </div>
      </motion.div>
      <Intro/>
    </div>
  );

  // ── DESKTOP ─────────────────────────────────────────────────────────────
  return (
    <div style={{ width:'100dvw', height:'100dvh', position:'relative', overflow:'hidden', background:'#01050e' }}>
      <BG/>
      <RoundBadge/>
      <motion.div animate={shakeControls} style={{
        position:'relative', zIndex:10, height:'100%',
        perspective: SCENE.perspective, perspectiveOrigin:'50% 38%',
      }}>
        <div style={{ height:'100%', display:'flex', flexDirection:'column', padding:'44px 30px 20px', gap:8, transformStyle:'preserve-3d' }}>
          <div style={{ display:'flex', justifyContent:'center', flexShrink:0, transformStyle:'preserve-3d' }}>
            {pos.top && (
              <div style={{ transform:seat('top'), transformStyle:'preserve-3d' }}>
                <PlayerBox player={pos.top} isActive={currentPlayer?.user_id===pos.top.user_id} variant="top"/>
              </div>
            )}
          </div>
          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, minHeight:0, transformStyle:'preserve-3d' }}>
            <div style={{ flexShrink:0, transform:seat('left'), transformStyle:'preserve-3d' }}>
              {pos.left && <PlayerBox player={pos.left} isActive={currentPlayer?.user_id===pos.left.user_id} variant="side"/>}
            </div>
            <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, minWidth:0 }}>
              <div style={{ textAlign:'center' }}><CardTable tableCards={tableCards} mobile={false}/></div>
              <TonTable active={canAct}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,auto)', columnGap:22, rowGap:26, justifyContent:'center', justifyItems:'center', transformStyle:'preserve-3d' }}>
                  {tableCards.map((card,i) => (
                    <TableCard key={i} card={card} index={i} canPick={canAct&&!card.revealed}
                      onPick={handlePickCard} mobile={false} isRevealing={revealing===i}/>
                  ))}
                </div>
              </TonTable>
              <Feedback/>
              <WaitingMsg/>
            </div>
            <div style={{ flexShrink:0, transform:seat('right'), transformStyle:'preserve-3d' }}>
              {pos.right && <PlayerBox player={pos.right} isActive={currentPlayer?.user_id===pos.right.user_id} variant="side"/>}
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8, flexShrink:0, transformStyle:'preserve-3d' }}>
            <JollyBtn/>
            {pos.bottom && (
              <div style={{ transform:seat('bottom'), transformStyle:'preserve-3d' }}>
                <PlayerBox player={pos.bottom} isActive={currentPlayer?.user_id===pos.bottom.user_id} variant="bottom"/>
              </div>
            )}
          </div>
        </div>
      </motion.div>
      <Intro/>
    </div>
  );
};

export default TonGame;
